// Frame + keyword-transition helpers for the DML scanner. Split out to keep
// classifyPosition.dml.ts under the project's 200-line budget.
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Position Classifier — DML.

import type { ResolverPositionKind } from '../types';

export type OuterKind = 'select' | 'with' | 'insert' | 'update' | 'delete' | 'merge' | 'unknown';

export interface Frame {
  clause: ResolverPositionKind;
  lastWasJoin: boolean;
  // Hint for the next pushed paren frame: insert-target / insert-values /
  // cte-def / null (= subquery). Cleared after the paren is pushed.
  nextParenClause: ResolverPositionKind | null;
  // Multi-word keyword buffer at this frame, newest-first. prevKw[0] is the
  // most recent keyword, prevKw[1] the one before, etc.
  prevKw: string[];
}

export function makeFrame(clause: ResolverPositionKind): Frame {
  return { clause, lastWasJoin: false, nextParenClause: null, prevKw: [] };
}

export function pushKeyword(frame: Frame, kw: string): void {
  frame.prevKw.unshift(kw);
  if (frame.prevKw.length > 5) frame.prevKw.length = 5;
}

export function pushFrame(stack: Frame[]): void {
  const parent = stack[stack.length - 1];
  // Priority: explicit hint > inherit insert-values (multi-row VALUES) >
  // default to subquery.
  let initialClause: ResolverPositionKind;
  if (parent.nextParenClause !== null) initialClause = parent.nextParenClause;
  else if (parent.clause === 'insert-values') initialClause = 'insert-values';
  else initialClause = 'subquery';
  parent.nextParenClause = null;
  stack.push(makeFrame(initialClause));
}

// eslint-disable-next-line complexity, max-lines-per-function
export function handleKeyword(
  frame: Frame,
  outerKind: OuterKind,
  kw: string,
): OuterKind {
  // First-keyword sniff at the outermost frame establishes outerKind.
  if (outerKind === 'unknown') {
    if (kw === 'SELECT') { frame.clause = 'select-list'; return 'select'; }
    if (kw === 'WITH') { frame.clause = 'unknown'; return 'with'; }
    if (kw === 'INSERT') { frame.clause = 'unknown'; return 'insert'; }
    if (kw === 'UPDATE') { frame.clause = 'from'; return 'update'; }
    if (kw === 'DELETE') { frame.clause = 'from'; return 'delete'; }
    if (kw === 'MERGE') { frame.clause = 'from'; return 'merge'; }
  }

  // Multi-word keyword detection via prevKw buffer.
  if (kw === 'SET'
      && frame.prevKw[0] === 'UPDATE'
      && frame.prevKw[1] === 'DO'
      && frame.prevKw[2] === 'CONFLICT') {
    frame.clause = 'update-set';
    return outerKind;
  }
  if (kw === 'SOURCE'
      && frame.prevKw[0] === 'BY'
      && frame.prevKw[1] === 'MATCHED'
      && frame.prevKw[2] === 'NOT'
      && frame.prevKw[3] === 'WHEN') {
    frame.clause = 'merge-when-not-matched-source';
    return outerKind;
  }
  if (kw === 'MATCHED' && frame.prevKw[0] === 'WHEN') {
    frame.clause = 'merge-when-matched';
    return outerKind;
  }

  // Single-keyword transitions.
  if (kw === 'SELECT') { frame.clause = 'select-list'; return outerKind; }
  if (kw === 'FROM') { frame.clause = 'from'; return outerKind; }
  if (kw === 'WHERE') {
    // Inside a MERGE WHEN body, the merge-when-* classification wins — the
    // sub-action's WHERE (DELETE/UPDATE) doesn't override the WHEN context.
    if (frame.clause === 'merge-when-matched' || frame.clause === 'merge-when-not-matched-source') {
      return outerKind;
    }
    if (outerKind === 'update') frame.clause = 'update-where';
    else if (outerKind === 'delete') frame.clause = 'delete-where';
    else frame.clause = 'where';
    return outerKind;
  }
  if (kw === 'HAVING') { frame.clause = 'having'; return outerKind; }
  if (kw === 'GROUP') { frame.clause = 'group-by'; return outerKind; }
  if (kw === 'ORDER') { frame.clause = 'order-by'; return outerKind; }
  if (kw === 'JOIN') { frame.clause = 'from'; frame.lastWasJoin = true; return outerKind; }
  if (kw === 'ON') {
    if (outerKind === 'merge') frame.clause = 'merge-on';
    else if (frame.lastWasJoin) frame.clause = 'join-on';
    return outerKind;
  }
  if (kw === 'INTO') {
    if (outerKind === 'insert' || outerKind === 'merge') frame.clause = 'from';
    if (outerKind === 'insert') frame.nextParenClause = 'insert-target';
    return outerKind;
  }
  if (kw === 'VALUES') {
    frame.clause = 'insert-values';
    frame.nextParenClause = 'insert-values';
    return outerKind;
  }
  if (kw === 'SET' && outerKind === 'update') {
    frame.clause = 'update-set';
    return outerKind;
  }
  if (kw === 'RETURNING') {
    frame.clause = 'select-list';
    return outerKind;
  }
  if (kw === 'USING' && outerKind === 'merge') {
    frame.clause = 'from';
    return outerKind;
  }
  if (kw === 'AS' && outerKind === 'with') {
    // The next '(' opens the CTE body — seed it as cte-def.
    frame.nextParenClause = 'cte-def';
    return outerKind;
  }
  // BY is folded into GROUP BY / ORDER BY / WHEN NOT MATCHED BY; no action.
  return outerKind;
}
